import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar } from '@capacitor/status-bar';
import { Box } from '@deckee/deck-hand';
import HyperDX from '@hyperdx/browser';
import {
  createBrowserHistory as createHistoryBrowser,
  createMemoryHistory as createHistoryMobileApp,
} from 'history';
import { DateTime } from 'luxon';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import AppErrorBoundary from './app-error-boundary';
import UpdateFromCordova from './components/null-components/UpdateFromCordova/UpdateFromCordova';

import Root from './components/root';
import { deckeeLogo, ReactSvg } from './components/shared/vector-images';
import Text from './components/shared/_deprecated/elements/Text/Text';
import { extractInvitationCodeFromUrl } from './extractInvitationCodeFromUrl';
import { initialize as initializeAppsFlyerService } from './services/appsflyer';
import {
  initialize as initializeBackendService,
  telemetry,
} from './services/backend';
import { initialize as initializeEmail } from './services/email';
import {
  captureEvent,
  initialize as initializeEventCapture,
} from './services/event-capture';
import { initialize as initializeFeatureService } from './services/feature';
import { initialize as initializeGeoService } from './services/geo';
import {
  get,
  initialize as initializeLocalStorageService,
  remove,
  set,
} from './services/local-storage';
import { initialize as initializeLogTripsService } from './services/log-trips';
import { initialize as initializeBackgroundGeoService } from './services/log-trips/background-geolocation';
import { initialize as initializeMapLayerService } from './services/map-layer';
import { initialize as initializeMapStyleService } from './services/map-style';
import { initialize as initializeMemberService } from './services/member';
import { initialize as initializeMessageBrokerService } from './services/message-broker';
import { initialize as initializePermissionsService } from './services/permissions';
import { initialize as initializeReverseGeocoding } from './services/reverse-geocoding';
import { initialize as initializeRouteService } from './services/route';
import captureSpecificError from './services/capture-error';
import { applyServerEnvironment } from './services/server-environment';
import { initialize as initializeSqlLiteDatabase } from './services/sql-lite';
import { initialize as initializeFacebookService } from './services/facebook';
import {
  getStore,
  initialize as initializeStoreService,
} from './services/store';
import { updateViewportSize } from './services/store/modules/app-controls';
import { setInvitationCode } from './services/store/modules/follows';
import { checkOfflineIncidentReport } from './services/store/modules/incident-report';
import { loadActiveTrip } from './services/store/modules/log-trips';
import { initialize as initializeUserService } from './services/user';
import {
  captureAppStartEvent,
  initialize as initializeUtilitiesService,
} from './services/utilities';
import { initialize as initializeWeather } from './services/weather';
import { initialize as initializeWhat3WordsService } from './services/what3words';
import theme from './theme';

require('./poly-fills');

const serviceName = 'app-main-client';
const environment =
  window.location.host === 'deckee.com' ? 'production' : 'staging';

const enableSessionReplay =
  typeof window?.DECKEE_CONFIG?.hyperdx?.sessionReplay === 'string'
    ? window?.DECKEE_CONFIG?.hyperdx?.sessionReplay === 'true'
    : !!window?.DECKEE_CONFIG?.hyperdx?.sessionReplay;

HyperDX.init({
  apiKey: window.DECKEE_CONFIG?.hyperdx?.apiKey,
  service: `${serviceName}-${environment}`,
  tracePropagationTargets: [/deckee.com\/public\/app-main\/api/i], // Set to link traces from frontend to backend requests
  consoleCapture: window.DECKEE_CONFIG?.hyperdx?.consoleCapture,
  advancedNetworkCapture: false,
  disableReplay: !enableSessionReplay,
  recordCanvas: enableSessionReplay,

  // Configure session recording to reduce noise
  sessionRecording: {
    // Ignore elements that frequently change and cause rate limiting
    ignoreSelector: [
      '[class*="mapbox"]', // Ignore Mapbox elements
      '[class*="animation"]', // Ignore animation elements
      '[class*="loading"]', // Ignore loading indicators
      'canvas', // Ignore canvas elements
      'video', // Ignore video elements
    ].join(','),
    // Reduce sampling rate to capture fewer mutations - possible next step
    // sampleRate: 0.1, // Only record 10% of sessions
  },
  instrumentations: {
    fetch: {
      ignoreUrls: [
        new RegExp(
          'mapbox|amplitude|google-analytics|hyperdx|icon-composer\\.io',
          'g',
        ),
      ],
    },
    xhr: {
      ignoreUrls: [
        new RegExp(
          'mapbox|amplitude|google-analytics|hyperdx|icon-composer\\.io',
          'g',
        ),
      ],
    },
  },
});

// ========================================================
// Register our service worker
// ========================================================
// BOOO!!! Service workers are not supported in cordova apps as the assets are loaded from file:
// Leaving here for future reference though.
// if ('serviceWorker' in navigator) {
//   window.addEventListener('load', () => {
//     navigator.serviceWorker.register('service-worker.js').then(registration => {
//       console.log('SW registered: ', registration);
//     }).catch(registrationError => {
//       console.log('SW registration failed: ', registrationError);
//     });
//   });
// }

let initializers = [];

const init = (initializerId, fn, params) => {
  const initializerResponse = fn(params);

  // If it's a promise, then attach a catch to it to capture any errors
  if (initializerResponse && initializerResponse.catch) {
    initializerResponse.catch((e) => {
      telemetry(
        telemetry.ERROR,
        `[startApplication] Non-Async Initializer errored, containers/app-main-client/src/app.jsx, ${initializerId} ${e.message} `,
      );
    });
  }

  initializers.push(initializerResponse);
  return initializerResponse;
};

const asyncInit = async (initializerId, fn, params) => {
  const initializerResponse = fn(params);

  const timeout = new Promise((res, rej) =>
    // oxlint-disable-next-line no-promise-executor-return -- legacy code
    setTimeout(() => {
      //  Resolve the timeout.
      rej(new Error('Timeout promise resolved'));
    }, 6000),
  );

  const fnOrTimeout = Promise.race([initializerResponse, timeout]);

  initializers.push(fnOrTimeout);
  try {
    await fnOrTimeout;
  } catch (e) {
    telemetry(
      telemetry.ERROR,
      `[startApplication] Initializer timed out or errored, containers/app-main-client/src/app.jsx, ${initializerId} ${e.message} `,
    );
  }
};

const MOUNT_NODE = document.getElementById('app');

const deckeeRender = (Content) => {
  SplashScreen.hide();
  ReactDOM.render(Content, MOUNT_NODE);
  remove('app', 'crashCount');
};

// Manually pollyfill allSettled to resolve issues being seen in error reports
Promise.allSettled =
  Promise.allSettled ||
  ((promises) =>
    Promise.all(
      promises.map((p) => {
        // If it's not a promise, just return it.
        if (!p || !p.then) {
          return p;
        }

        // Return the promise and resolve how "allSettled" expects
        return p
          .then((value) => ({
            status: 'fulfilled',
            value,
          }))
          .catch((reason) => ({
            status: 'rejected',
            reason,
          }));
      }),
    ));

const setAppStartTimeToDeviceStorage = () => {
  // Using luxon, set the yesterdays date time to local storage
  const today = DateTime.local();
  set('app', 'lastAppStartDate', today.toISO());
};

const startApplicationPrivate = async ({
  platform,
  version,
  runningEnvironment,
  bootstrapperService,
}) => {
  // oxlint-disable-next-line no-console -- legacy code. should use centralised logging instead
  console.log('Preview feature test - app-main-client starting');
  // Check if we are on cordova and redirect to the update app message
  if (runningEnvironment === 'cordova') {
    const { osVersion, operatingSystem } = await Device.getInfo();

    deckeeRender(
      <AppErrorBoundary>
        <ThemeProvider theme={theme}>
          <UpdateFromCordova
            platform={platform}
            osVersion={osVersion}
            operatingSystem={operatingSystem}
          />
        </ThemeProvider>
      </AppErrorBoundary>,
    );
    return;
  }

  // The capacitor docs indicate that you should show the status bar as soon as you can. Suspect it has something to do with
  // getting capacitor in sync with the app 🤷🏼‍♀️
  if (Capacitor.isNativePlatform()) {
    StatusBar.show();
  }
  // Attach all capacitor exported plugins onto the window object -
  // just for compatibility with the previous cordova implementation
  // if (window.CapExp) {
  //   Object.keys(window.CapExp).forEach((k) => {
  //     window[k] = window.CapExp[k];
  //   });
  // }

  // ========================================================
  // Store and History Instantiation
  // ========================================================

  const initialState = {
    configurations: {
      platform,
      version,
      runningEnvironment,
    },
  };

  // When running in cordova, we required a memory router rather than a browser router (why?)
  // A memory router will default the initial entry as "/", but we require it to either be
  // /maps or the deep link being provided. To do that, we should parse the location url
  // and extract the path name

  // Check to make sure the path includes /maps at the start
  let browserRouterInitialEntry = '/maps';

  if (
    window.location.pathname &&
    window.location.pathname.indexOf('/maps') === 0
  ) {
    browserRouterInitialEntry = window.location.pathname;
  }

  const history =
    runningEnvironment === 'capacitor'
      ? createHistoryMobileApp({ initialEntries: [browserRouterInitialEntry] })
      : createHistoryBrowser();

  // Hack :cry
  window.appHistory = history;

  // ========================================================
  // Service layer dependency injection
  // ========================================================

  // This is our newer service layer that no longer uses the react context due to the react
  // development team taking a path that doesn't sit well for standard dependency injection
  // practices. We're now using more of a poor-man's container system where we just keep state in
  // a given module and import it to use it. It's a bit yuck, but the context API for react is even
  // worse and it's constraints don't provide any actual value.
  // The order here matters, for any service which needs to complete it's initialization for other
  // services to use (e.g. the store), use the asyncInit function. For any service which doesn't
  // have any dependencies, use the init function. These functions decorate the initializers with
  // exception handling and timeout handling.
  initializers = [];
  await init('initializeLocalStorageService', initializeLocalStorageService);

  if (await applyServerEnvironment()) {
    return;
  }

  init('initializePermissionsService', initializePermissionsService);
  // Requires the permissions service
  await asyncInit('initializeSqlLiteDatabase', initializeSqlLiteDatabase, true);
  await asyncInit('initializeFacebookService', initializeFacebookService);
  // Required by backend and a number of other services
  // This must be initialized after the SQL Lite database as it internally uses event capture
  init('initializeStoreService', initializeStoreService, { initialState });

  // Local storage is used by backend service.
  await asyncInit('initializeMapLayerService', initializeMapLayerService);
  // Required by most of the app due to the use of telemetry
  await asyncInit('initializeBackendService', initializeBackendService);
  init('initializeRouteService', initializeRouteService, { history });
  init('initializeFeatureService', initializeFeatureService);
  await asyncInit('initializeLogTripsService', initializeLogTripsService);
  init('initializeGeoService', initializeGeoService);
  init('initializeBackgroundGeoService', initializeBackgroundGeoService);
  init('initializeMapStyleService', initializeMapStyleService);
  await asyncInit('initializeMemberService', initializeMemberService);
  initializeMessageBrokerService();
  await asyncInit('initializeEventCapture', initializeEventCapture);
  await asyncInit('initializeUserService', initializeUserService); // Needs to be initialised for the weather service
  init('initializeUtilitiesService', initializeUtilitiesService, {
    bootstrapperService,
    history,
  }); // Requires the event capture server (amplitude)
  init('initializeWhat3WordsService', initializeWhat3WordsService);
  init('initializeReverseGeocoding', initializeReverseGeocoding);
  init('initializeEmail', initializeEmail);
  await asyncInit('initializeWeather', initializeWeather);
  init('initializeAppsFlyerService', initializeAppsFlyerService);

  await Promise.allSettled(initializers).then((results) =>
    results.forEach((result) => {
      if (result?.status === 'rejected') {
        const errorString = `App Initialize Failure: ${result.reason}`;
        telemetry(telemetry.WARN, errorString);
      }
    }),
  );

  // Don't wait for this, it can run in parallel
  captureAppStartEvent();

  const store = getStore();
  window.appStore = store;

  // This action is an async action, but we don't want to wait for it to complete, or do anything (here) if it fails.
  // The action itself will report the error if there is an issue
  store.dispatch(checkOfflineIncidentReport());

  // Check the query params for an invitation code and add it to the store if it exists
  const addInvitationCodeFromUrlToStore = (fullUrl) => {
    const invitationCode = extractInvitationCodeFromUrl(fullUrl);

    if (invitationCode) {
      store.dispatch(setInvitationCode(invitationCode));
    }
  };

  addInvitationCodeFromUrlToStore(window.location.href);

  setAppStartTimeToDeviceStorage();

  App.addListener('pause', () => {
    remove('app', 'hasShownSignalSlider');
    captureEvent({
      type: 'app: pause',
      payload: {},
    });
  });
  App.addListener('resume', () => {
    let reloadApp = false;
    store.dispatch(loadActiveTrip());
    // Using luxon, check to see if the variable `appStartDate` is yesterday or earlier.
    const rightNow = DateTime.local();
    const lastLoadDate = DateTime.fromISO(get('app', 'lastAppStartDate'));

    if (!lastLoadDate.isValid) {
      setAppStartTimeToDeviceStorage();
    } else {
      const daysSinceAppStartDate = rightNow.diff(lastLoadDate, 'days').days;
      if (daysSinceAppStartDate >= 1) {
        reloadApp = true;
      }
    }

    captureEvent({
      type: 'app: resume',
      payload: { reloadApp },
    });

    store.dispatch(updateViewportSize());
    if (reloadApp) {
      window.location.reload();
    }
  });

  App.addListener('appUrlOpen', async (eventData) => {
    const currentUrl = new URL(window.location.href);
    const newUrl = new URL(eventData.url);

    // Merge all the query params
    const paramsArray = [];
    currentUrl.searchParams.forEach((value, key) => {
      paramsArray.push(`${key}=${value}`);
    });
    newUrl.searchParams.forEach((value, key) => {
      paramsArray.push(`${key}=${value}`);
    });
    const mergedQueryParams = paramsArray.join('&');

    try {
      await Browser.close();
    } catch (e) {
      // This error will occur if there is no active window to close so we ignore it atm
      // >> e = 'Error: No active window to close!'
    }

    history.push(`${newUrl.pathname}?${mergedQueryParams}`);
    addInvitationCodeFromUrlToStore(eventData.url);
  });

  // ========================================================
  // Render root component with the dependencies we've created.
  // ========================================================
  deckeeRender(
    <AppErrorBoundary>
      <Provider store={store}>
        <Root history={history} />
      </Provider>
    </AppErrorBoundary>,
  );

  telemetry(
    telemetry.DEBUG,
    `[app] starting application for platform ${platform}, with version: ${version}`,
  );
};

const startApplication = async ({
  platform,
  version,
  runningEnvironment,
  bootstrapperService,
}) => {
  startApplicationPrivate({
    platform,
    version,
    runningEnvironment,
    bootstrapperService,
  }).catch(async (e) => {
    const crashCount = ((await get('app', 'crashCount')) || 0) + 1;
    set('app', 'crashCount', crashCount);
    captureSpecificError(
      e,
      'containers/app-main-client/src/app.jsx',
      {
        deckeeId: '[startApplication] Error starting application',
      },
      {
        platform,
        version,
        runningEnvironment,
      },
    );

    // Attempt to capture an event to amplitude. Not using the typicall captureEvent because it has a lot of dependancies
    // We also want to ignore if this fails
    // NOTE: Commenting this out because capture event has a dependancy on the store, which we don't want to try and initialize here.
    // await captureEventAmplitude({
    //   type: 'app: failed to start',
    //   payload: {
    //     crashCount,
    //   },
    // }).catch(console.warn);

    if (crashCount < 3) {
      window.location.reload();
      return;
    }
    deckeeRender(
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        p="4"
      >
        <ReactSvg
          toDisplay={deckeeLogo()}
          options={{
            fillColor: '#FF5C39',
          }}
          width={70}
          height={70}
        />

        <Text size="2" align="center" type="light">
          Mayday!
          <br />
          Something went wrong.
        </Text>
        <Text size="0" align="center" type="alternate-light">
          Please try restarting the DECKEE app
        </Text>
      </Box>,
    );
  });
};

window.startApplication = startApplication;
